import { QRCodeSVG } from "qrcode.react";

/**
 * Render a QR code for `value` as a crisp, scalable <svg>. The matrix comes from
 * the battle-tested `qrcode.react` library (`QRCodeSVG`) — a hand-rolled encoder
 * previously lived here but produced UNSCANNABLE codes for the real ~469-char
 * check-in token (it reached QR version 17 but never wrote the version-information
 * modules that QR requires for versions ≥7, so a BarcodeDetector decoded it to
 * null). The library is the single QR path (see clean-migration): no second copy.
 *
 * The SVG is `aria-hidden` so the encoded credential is never exposed as an
 * accessible name. A presenter should provide a non-secret accessible label on
 * a wrapper. The raw value is deliberately not copied into any DOM attribute;
 * it exists only as input to the QR encoder.
 *
 * Scannability choices (all max-contrast / max-reliability):
 *   - level "M" — medium error correction, the check-in standard.
 *   - marginSize 4 — the spec-required 4-module quiet zone (was 2 in the old
 *     renderer); scanners need it to lock on.
 *   - pure #000 on #fff — NOT var(--ink)/var(--bone). A near-black/near-white or
 *     CSS-variable colour cuts the contrast a scanner relies on.
 */
export function QrCode({
  value,
  // Default rendered size. The real ~469-byte check-in JWT encodes to a dense
  // matrix, so render larger than a typical default to keep each module big
  // enough to scan reliably. Callers can still override.
  size = 264,
  className,
}: {
  value: string;
  size?: number;
  className?: string;
}) {
  return (
    <QRCodeSVG
      value={value}
      size={size}
      level="M"
      marginSize={4}
      bgColor="#ffffff"
      fgColor="#000000"
      className={className}
      aria-hidden="true"
      shapeRendering="crispEdges"
    />
  );
}
